"use client";
import React, { useState, useEffect } from "react";
import { motion, Variants } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { Search, Calendar, Clock } from "lucide-react";
import ReservationForm from "@/components/menu/reservation-form/ReservationForm";

interface Topic {
  title: string;
  description: string;
}

interface Blog {
  id: number;
  title: string;
  excerpt: string;
  image: string;
  date: string;
  readTime: string;
  category: string;
  author: string;
  content: string;
  relatedArticles: number[];
  topics: Topic[];
}

export default function BlogPage() {
  const [blogData, setBlogData] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Fetch blog data from JSON file
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await fetch("/blogsData.json");
        if (!response.ok) {
          throw new Error("Failed to fetch blog data");
        }
        const data = await response.json();
        setBlogData(data);
      } catch (error) {
        console.error("Error fetching blog data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  // Dynamically generate categories from blog data
  const categories = [
    "All",
    ...Array.from(new Set(blogData.map((blog) => blog.category))),
  ];

  const fadeUp: Variants = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  // Filter blogs based on search and category
  const filteredBlogs = blogData.filter((blog) => {
    const matchesSearch =
      blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      blog.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === "All" || blog.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <section className="min-h-screen flex items-center justify-center">
        <div className="text-[#D4AF37] text-xl font-light">
          Loading blogs...
        </div>
      </section>
    );
  }

  return (
    <section className="">
      <div className="relative rounded-b-4xl overflow-hidden">
        {/* Background Image */}
        <Image
          width="1440"
          height="732"
          className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none"
          src="/imgs/blogs-bg.png"
          alt="Blogs-BG"
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-[#000000]/45 pointer-events-none"></div>

        {/* Header Content */}
        <motion.div
          className="relative z-10 px-5 pt-40 py-20"
          initial="hidden"
          animate="visible"
          variants={{
            visible: {
              transition: {
                staggerChildren: 0.2,
              },
            },
          }}
        >
          <motion.h2
            variants={fadeUp}
            className="text-[#FFFFFF] text-2xl md:text-5xl text-center font-semibold font-serif"
          >
            Stories from Our Table
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="text-[#FFFFFF] text-sm md:text-lg text-center font-light mt-4"
          >
            A closer look at the flavors, people, and traditions that shape
            Almajlis
          </motion.p>

          {/* Search Bar */}
          <motion.div variants={fadeUp} className="max-w-xl mx-auto mt-10">
            <div className="relative">
              <input
                type="text"
                placeholder="Search any Blog"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-6 py-3.5 pr-12 rounded-lg bg-black/[31%] backdrop-blur-[23.74px] text-[#D4AF37] placeholder-old-lace focus:outline-none border border-old-lace/60 font-light"
              />
              <button className="absolute right-2 top-1/2 -translate-y-1/2 text-old-lace p-2.5 rounded-md transition-colors">
                <Search size={18} />
              </button>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Category Filter */}
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10">
        <div className="flex flex-wrap gap-3 justify-center">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`tab_btn transform transition duration-300 ease-in-out ${
                selectedCategory === category
                  ? "tab_btn_active"
                  : "tab_btn_inactive"
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Blog Grid */}
      <div className="max-w-7xl mx-auto px-5 lg:px-10 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBlogs.map((blog, index) => (
            <Link
              href={`/blogs/${blog.id}`}
              key={blog.id}
              className="group bg-[#FAF6DB]/15 rounded-[40px] overflow-hidden border border-[#2A2A2A] hover:border-[#D4AF37] transition-all duration-300 cursor-pointer"
            >
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {/* Blog Image */}
                <div className="relative h-56 overflow-hidden">
                  <Image
                    width="510"
                    height="267"
                    className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none group-hover:scale-105 transition-transform duration-300"
                    src={blog.image}
                    alt={blog.title}
                  />
                </div>

                {/* Blog Content */}
                <div className="p-6">
                  <div className="flex items-center justify-between gap-4 text-[#D4AF37] text-sm mb-3">
                    <div className="flex items-center gap-1.5">
                      <Calendar size={14} />
                      <span>{blog.date}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock size={14} />
                      <span>{blog.readTime}</span>
                    </div>
                  </div>

                  <h3 className="text-white text-xl font-semibold mb-3 group-hover:text-[#D4AF37] transition-colors">
                    {blog.title}
                  </h3>

                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {blog.excerpt}
                  </p>

                  {/* Read More Button */}
                  <div className="flex items-center text-[#D4AF37] font-semibold text-sm group-hover:gap-2 transition-all">
                    Read Full Article
                    <span className="group-hover/btn:translate-x-3 transition-transform ml-2">
                      →
                    </span>
                  </div>
                </div>
              </motion.div>
            </Link>
          ))}
        </div>

        {/* No Results */}
        {filteredBlogs.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg">
              No blogs found matching your search.
            </p>
          </div>
        )}
      </div>

      <ReservationForm />
    </section>
  );
}
